import * as React from "react";
import { connect } from "react-redux";
import idssStyle from "../../Configuration/SharedStyling";
import {
  Grid2 as Grid,
  Card,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Fade,
  CircularProgress,
  Button,
  CardContent,
  IconButton,
  Tooltip
} from "@mui/material";
import SharedMaterialReactTable, { ISharedTableColumn } from "../Shared/MaterialReactTable";
import { Theme } from '@mui/material/styles';
import { createStyles, withStyles, WithStyles } from '@mui/styles';
import UserManagerService from "../Services/UserManagerService";
import { AjaxError } from "rxjs/ajax";
import {
  IGenericUserProps,
  genericMapStateToProps
} from "../../DataClasses/GenericUser";
import { Formik, FormikHelpers, FormikErrors, FormikProps } from "formik";
import * as validator from "validator";
import { allUsersMachine } from '../../Components/Machines/AllUsersMachine';
import { createActor } from "xstate";
import AlertDialog from "../Shared/AlertDialog";
import ErrorDialog from "../Shared/ErrorDialog";
import { IUserRolePayload } from "../../DataClasses/UsersData";
import { IOrgSummary } from "../../DataClasses/SubmissionData";
import Select from "react-select";
import ExceptionSerivce from "../Services/ExceptionService";
import SubmissionsService from "../Services/SubmissionsService";
import { SeedTime } from "../../Configuration/Setting";
import { Redirect } from "react-router-dom";
import { Cached, RotateLeft } from "@mui/icons-material";
import { firstValueFrom } from "rxjs";

const styles = (theme: Theme) => createStyles({
  ...idssStyle(theme),
  spacer: {
    paddingLeft: '10px'
  },
  progress: {
    verticalAlign: 'middle',
    paddingLeft: '5px'
  }
});

// REDUX PROPS
interface IAllUsersViewStateStore {
  data: any[];
  isLoading: boolean;
  stateMachine: any; // Simplified type for XState v5 compatibility
  manageUsersPayload: IUserRolePayload[];
  filterText: string;
  targetUser: string;
  allOrgs: IOrgSummary[];
  allVendors: IOrgSummary[];
  allSysGroups: string[];
  selectedSysGroup: any;
  selectedOrgs: any;
  selectedRoles: any;
  allRoles: string[];
  inputSubIds: string;
  errorMsg: string;
  redirect: boolean;
  isProcessing: boolean;
  isUserLockedOut: boolean;
  reloadingUmCache: boolean;
}

interface IAddUserForm {
  email: string;
  firstName: string;
  lastName: string;
}

type IStylesProps = WithStyles<typeof styles>;

// VIEW
class AllUsersView extends React.Component<
  IGenericUserProps & IStylesProps,
  IAllUsersViewStateStore
> {
  private service: any;

  constructor(props: IGenericUserProps & IStylesProps) {
    super(props);
    this.state = {
      data: [],
      isLoading: true,
      stateMachine: allUsersMachine.initialState,
      manageUsersPayload: [],
      targetUser: "",
      filterText: "",
      allOrgs: [],
      allVendors: [],
      allSysGroups: [],
      selectedSysGroup: "",
      selectedOrgs: "",
      selectedRoles: "",
      allRoles: [],
      inputSubIds: "",
      errorMsg: "",
      redirect: false,
      isProcessing: false,
      isUserLockedOut: false,
      reloadingUmCache: false
    };

    // XState v5 API - use createActor instead of interpret
    this.service = createActor(allUsersMachine);
    this.service.subscribe((state: any) => {
      this.setState({ stateMachine: state });
    });
  }

  public async componentDidMount() {
    // This method runs when the component is first added to the page
    await this.RefreshModel();
    await this.RefreshManageUsersModel();

    // Start the State machine
    this.service.start();
  }

  public async componentWillUnmount() {

    // Stop the State machine
    this.service.stop();
  }
  private redirectToErrorPage() {
    return <Redirect to='/errorpage' />;
  }

  private setRedirectState() {
    this.setState({ redirect: true });
  }

  private usersBodyTemplate = (rowData: any) => {
    const { classes } = this.props;
    return (
      <Button className={classes.linkButton} onClick={() => this.DoManageUser(rowData.user)}>
        {rowData.user}
      </Button>
    );
  };

  private actionBodyTemplate = (rowData: any) => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={() => this.deleteButtonClick(rowData.action)}
      >
        Delete
      </Button>
    );
  };

  // Define table columns for users list
  private getUsersTableColumns = (): ISharedTableColumn<any>[] => [
    {
      accessorKey: 'user',
      header: 'Users',
      enableSorting: true,
      enableFiltering: true,
      Cell: ({ row }) => this.usersBodyTemplate(row.original),
    },
  ];

  // Define table columns for manage users
  private getManageUsersTableColumns = (): ISharedTableColumn<any>[] => [
    {
      accessorKey: 'entityId',
      header: 'Entity ID',
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'entityName',
      header: 'Org/Vendor',
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'roleType',
      header: 'Role Type',
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'roleName',
      header: 'Role Name',
      enableSorting: true,
      enableFiltering: true,
    },
    {
      accessorKey: 'action',
      header: 'Action',
      enableSorting: false,
      enableFiltering: false,
      enableColumnActions: false,
      Cell: ({ row }) => this.actionBodyTemplate(row.original),
    },
  ];

  public render() {
    if (this.state.redirect) {
      return this.redirectToErrorPage();
    }

    const { classes } = this.props;

    return (
      <Grid container={true} className={classes.gridRowFlex} spacing={2}>
        <Grid key={1} size={1}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" color="primary">
                Add Users
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => this.service.send({ type: 'OpenUserDialog' })}
                className={classes.minspacing}
              >
                Add
              </Button>
              <Formik<IAddUserForm>
                initialValues={{
                  firstName: "",
                  lastName: "",
                  email: ""
                }}
                enableReinitialize={true}
                onSubmit={this.onSubmit}
                validate={this.validate}
              >
                {props => this.getForm(props, classes)}
              </Formik>
            </CardContent>
          </Card>
        </Grid>
        <Grid key={2} size={4}>
          <div className={classes.card}>
            <Fade in={!this.state.isLoading} {...(!this.state.isLoading
              ? { timeout: SeedTime }
              : {})}><div>
                <SharedMaterialReactTable
                  data={this.state.data.map(user => ({ user: user[0] }))}
                  columns={this.getUsersTableColumns()}
                  title="Available Users"
                  isLoading={this.state.isLoading}
                  enablePagination={true}
                  enableSorting={true}
                  enableFiltering={true}
                  enableColumnActions={true}
                  enableRowActions={false}
                  initialState={{
                    pagination: { pageIndex: 0, pageSize: 25 },
                  }}
                  muiPaginationProps={{
                    rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
                  }}
                  emptyMessage="No users found."
                />
              </div>
            </Fade>
          </div>
        </Grid>
        {this.renderManageUsers()}
      </Grid>
    );
  }

  public renderManageUsers() {
    const { classes } = this.props;
    const tableData = this.state.manageUsersPayload.map(item => ({
      entityId: item.entityId,
      entityName: item.entityName,
      roleType: item.roleType,
      roleName: item.roleName,
      action: item.entityId + "," + item.roleType + "," + item.roleName
    }));
    const sysGroupOption = this.state.allSysGroups.map(m => ({
      label: m,
      value: m
    }));
    const orgOption = this.state.allOrgs.map(m => ({
      label: m.orgId + "-" + m.orgName,
      value: m.orgId
    }));
    const vendorOption = this.state.allVendors.map(m => ({
      label: m.orgId + "-" + m.orgName,
      value: m.orgId
    }));
    const roleOption = this.state.allRoles.map(m => ({
      label: m,
      value: m
    }));
    
    // Safe null checks for stateMachine.matches calls
    let addRoles = this.state.stateMachine?.matches?.("addRolesToUserDialogOpen") || false;
    let delleteAll = this.state.stateMachine?.matches?.("deleteAllAlertDialogOpen") || false;
    let manageUsers = this.state.stateMachine?.matches?.("manageUserOpen") || false;
    
    if (addRoles || delleteAll || manageUsers) {
      return <Grid key={3} >
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" color="primary">
              Manage Users ({this.state.targetUser})
              <Tooltip title="Reload Um Cache for this User" aria-label="Delete">
                <IconButton size='medium' >
                  {this.state.reloadingUmCache ?
                    <RotateLeft fontSize="inherit"></RotateLeft>
                    :
                    <Cached fontSize="inherit" onClick={async () => {
                      await this.reloadUmCache4TargetUser()
                    }} className={classes.button}></Cached>
                  }
                </IconButton >
              </Tooltip>
            </Typography>

            <Button className={classes.minspacing}
              variant="contained"
              color="primary"
              onClick={() => this.service.send({ type: "OpenDeleteAllAlertDialog" })}
            >
              Delete All
            </Button>
            <span className={classes.spacer}></span>
            <Button
              variant="contained"
              color="primary"
              onClick={() => this.service.send({ type: "OpenAddRolesToUserDialog" })}
            >
              Add
            </Button>
            <span className={classes.spacer}></span>
            {!this.state.isUserLockedOut ?
              <Button
                variant="contained"
                color="primary"
                onClick={() => this.updateUser('lock')} disabled={this.state.isProcessing}>
                Lock User (Testing)
              </Button>
              :
              <Button
                variant="contained"
                color="primary"
                onClick={() => this.updateUser('unlock')} disabled={this.state.isProcessing}>
                Unlock User
              </Button>}
            {this.state.isProcessing ? <span className={classes.progress}><CircularProgress color="primary" size={22} /></span> : null}
            <Dialog open={this.state.stateMachine?.matches?.("addRolesToUserDialogOpen") || false} onClose={() => this.service.send({ type: "CloseAddRolesToUserDialog" })} classes={{ paper: classes.dialogM }} >
              <DialogTitle id="max-width-dialog-title">
                Add Roles to the User ( {this.state.targetUser}):
              </DialogTitle>
              <DialogContent>
                <table>
                  <tbody>
                    <tr>
                      <td style={{ width: "240px" }}>Select System Group:</td>
                      <td style={{ width: "80%" }}>
                        <Select
                          options={sysGroupOption}
                          placeholder="Select a System Group"
                          isMulti={false}
                          value={this.state.selectedSysGroup}
                          closeMenuOnSelect={false}
                          isSearchable={true}
                          onChange={this.selectedSysGroupChange.bind(this)}
                        />
                      </td>
                    </tr>
                    <tr hidden={this.state.selectedSysGroup === ""}>
                      <td>Select Role(s):</td>
                      <td>
                        <Select
                          options={roleOption}
                          placeholder="Select role(s)"
                          isMulti={true}
                          value={this.state.selectedRoles}
                          closeMenuOnSelect={false}
                          isSearchable={true}
                          onChange={this.selectedRolesChange.bind(this)}
                        />
                      </td>
                    </tr>
                    <tr
                      hidden={this.state.selectedSysGroup.value !== "organization"}
                    >
                      <td style={{ width: "240px" }}>Select Organization(s):</td>
                      <td style={{ width: "80%" }}>
                        <Select
                          options={orgOption}
                          placeholder="Select organization(s) [OrgId-OrgName]"
                          isMulti={true}
                          value={this.state.selectedOrgs}
                          closeMenuOnSelect={false}
                          isSearchable={true}
                          onChange={this.selectedOrgsChange.bind(this)}
                        />
                      </td>
                    </tr>
                    <tr hidden={this.state.selectedSysGroup.value !== "vendor"}>
                      <td style={{ width: "240px" }}>Select Vendor(s):</td>
                      <td style={{ width: "80%" }}>
                        <Select
                          options={vendorOption}
                          placeholder="Select vendor(s) [OrgId-OrgName]"
                          isMulti={true}
                          value={this.state.selectedOrgs}
                          closeMenuOnSelect={false}
                          isSearchable={true}
                          onChange={this.selectedOrgsChange.bind(this)}
                        />
                      </td>
                    </tr>
                    <tr hidden={this.state.selectedSysGroup.value !== "submission"}>
                      <td style={{ width: "240px" }}>SubIds (comma separated):</td>
                      <td>
                        <input
                          value={this.state.inputSubIds}
                          onChange={e => this.inputSubIdsChange(e)}
                          style={{ width: "100%" }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </DialogContent>
              <DialogActions>
                <Button
                  className={classes.button}
                  onClick={this.handleSave}
                  disabled={
                    !(
                      (this.state.selectedSysGroup.value === "organization" &&
                        this.state.selectedRoles !== "" &&
                        this.state.selectedOrgs !== "") ||
                      (this.state.selectedSysGroup.value === "submission" &&
                        this.state.selectedRoles !== "" &&
                        this.state.inputSubIds !== "") ||
                      (this.state.selectedSysGroup.value === "system" &&
                        this.state.selectedRoles !== "") ||
                      (this.state.selectedSysGroup.value === "vendor" &&
                        this.state.selectedRoles !== "" &&
                        this.state.selectedOrgs !== "")
                    )
                  }
                >
                  Save
                </Button>
                <Button className={classes.disabledButton} onClick={this.handleClose}>
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>
            <AlertDialog
              showDialog={this.state.stateMachine?.matches?.("deleteAllAlertDialogOpen") || false}
              title="Delete Confirmation"
              description="Are you sure you want to delete all the roles for this user?"
              button1Text="Yes"
              button2Text="No"
              button1Handler={this.buttonYesHandler}
              button2Handler={() => this.service.send({ type: "CloseDeleteAllAlertDialog" })}
            />
            <ErrorDialog
              showError={this.state.errorMsg !== ""}
              errorMsg={this.state.errorMsg}
              buttonHandler={() => this.setState({ errorMsg: "" })}
            />
            <SharedMaterialReactTable
              data={tableData}
              columns={this.getManageUsersTableColumns()}
              title="Assigned Roles"
              isLoading={false}
              enablePagination={true}
              enableSorting={true}
              enableFiltering={true}
              enableColumnActions={true}
              enableRowActions={false}
              initialState={{
                pagination: { pageIndex: 0, pageSize: 25 },
              }}
              muiPaginationProps={{
                rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
              }}
              emptyMessage="No roles found."
            />
          </CardContent>
        </Card>
      </Grid>
    }
    else {
      return null;
    }

  }


  //#region Manage Users Region

  private async RefreshManageUsersModel() {

    try {
      const allOrgsRetval = await firstValueFrom(
        SubmissionsService.GetOrgForSubmissionsSummary$(this.props.user)
      );
      this.setState({ allOrgs: allOrgsRetval });
    } catch (ex) {
      const error = ex as AjaxError;
      this.setRedirectState();
      this.setState({
        errorMsg: ExceptionSerivce.StandardAjaxExceptionHandler(error)
      });
    }
    const allVendorsRetval = await firstValueFrom(
      SubmissionsService.GetOrgForVendorsSummary$(this.props.user)
    );
    if (allVendorsRetval instanceof AjaxError || !allVendorsRetval) {
      this.setRedirectState();
    } else {
      this.setState({ allVendors: allVendorsRetval });
    }
    const allSysGroupRetval = await firstValueFrom(
      UserManagerService.GetAllSysGroups$(this.props.user)
    );
    if (allSysGroupRetval instanceof AjaxError || !allSysGroupRetval) {
      this.setRedirectState();
    } else {
      this.setState({ allSysGroups: allSysGroupRetval });
    }
    // this.setState({ isLoading: false });
  }

  selectedOrgsChange(newValue): void {
    this.setState({ selectedOrgs: newValue });
  }
  async selectedSysGroupChange(newValue) {
    this.setState({
      selectedSysGroup: newValue,
      selectedOrgs: "",
      selectedRoles: "",
      inputSubIds: ""
    });
    const rolesRetval = await firstValueFrom(
      UserManagerService.GetAllRolesPerSysGroup$(this.props.user, newValue.value)
    );
    if (rolesRetval instanceof AjaxError || !rolesRetval) {
      this.setRedirectState();
    } else {
      this.setState({ allRoles: rolesRetval });
    }
  }
  selectedRolesChange(newValue): void {
    this.setState({ selectedRoles: newValue });
  }
  inputSubIdsChange(e: any): void {
    this.setState({ inputSubIds: e.target.value });
  }
  async deleteButtonClick(userRoleInfo: string) {
    try {
      await firstValueFrom(
        UserManagerService.DeleteUserRole$(this.props.user, this.state.targetUser, userRoleInfo)
      );
    } catch (ex) {
      const error = ex as AjaxError;
      this.setRedirectState();
      this.setState({
        errorMsg: ExceptionSerivce.StandardAjaxExceptionHandler(error)
      });
    }
    await this.RefreshRolesForUser();
  }
  private buttonYesHandler = async () => {
    this.service.send({ type: "CloseDeleteAllAlertDialog" });
    // dispatch delete all user roles action
    await firstValueFrom(
      UserManagerService.DeleteAllUserRoles$(
        this.props.user,
        this.state.targetUser
      )
    );
    await this.RefreshRolesForUser();
  };
  handleClose = () => {
    this.service.send({ type: "CloseAddRolesToUserDialog" });
    this.setState({
      selectedOrgs: "",
      selectedRoles: "",
      selectedSysGroup: "",
      inputSubIds: ""
    });
  };

  handleSave = async () => {
    await firstValueFrom(
      UserManagerService.AddUserToRoles$(
        this.props.user,
        this.state.targetUser,
        this.state.selectedSysGroup.value,
        this.state.selectedRoles.map(p => p.value).join(","),
        this.state.selectedOrgs === ""
          ? ""
          : this.state.selectedOrgs.map(p => p.value).join(","),
        this.state.inputSubIds,
        false
      )
    );
    await this.RefreshRolesForUser();
    this.service.send({ type: "CloseAddRolesToUserDialog" });
    this.setState({
      selectedOrgs: "",
      selectedRoles: "",
      selectedSysGroup: "",
      inputSubIds: ""
    });
  };

  private async reloadUmCache4TargetUser() {
    this.setState({ reloadingUmCache: true });
    await firstValueFrom(UserManagerService.ReloadUmCacheForUser$(this.props.user, this.state.targetUser));
    await this.RefreshRolesForUser();
    this.setState({ reloadingUmCache: false });
  }
  private async RefreshRolesForUser() {

    try {
      const retval = await firstValueFrom(
        UserManagerService.GetRolesForUser$(this.props.user, this.state.targetUser)
      );
      this.setState({ manageUsersPayload: retval });
    } catch (ex) {
      const error = ex as AjaxError;
      this.setRedirectState();
      this.setState({
        errorMsg: ExceptionSerivce.StandardAjaxExceptionHandler(error)
      });
    }
  }

  //#endregion 

  private async DoManageUser(targetName: string) {
    this.setState({ isUserLockedOut: false });
    this.setState(
      { targetUser: targetName },
      () => {
        this.RefreshRolesForUser();
        this.service.send({ type: "OpenManageUser" });
      }
    );

    //get user locked out status
    const retVal = await firstValueFrom(
      UserManagerService.IsUserLockedOut$(this.props.user, targetName)
    );
    this.setState({ isUserLockedOut: retVal });
  }
  private async RefreshModel() {
    this.setState({ isLoading: true });
    const retval = await firstValueFrom(
      UserManagerService.GetAllUsers$(this.props.user)
    );
    if (retval instanceof AjaxError || !retval) {
      this.setRedirectState();
    } else {
      const data = retval.map(item => {
        return [item];
      });
      this.setState({ data: data });
    }
    this.setState({ isLoading: false });
  }


  private getForm = (formikProps: FormikProps<IAddUserForm>, classes: any): JSX.Element => {
    const {
      handleSubmit,
      handleReset,
      handleChange,
      handleBlur,
      touched,
      errors,
      values,
      isSubmitting,
      isValid,
      submitForm
    } = formikProps;

    return (
      <div>
        <form onSubmit={handleSubmit}>
          <Dialog open={this.state.stateMachine?.matches?.('addUserDialogOpen') || false} onClose={handleReset}>
            <DialogTitle>Add New User</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus={false}
                fullWidth={true}
                label="Email"
                name="email"
                margin="dense"
                type="text"
                required={true}
                error={touched.email && Boolean(errors.email)}
                helperText={
                  touched.email && Boolean(errors.email)
                    ? errors.email
                    : undefined
                }
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                disabled={isSubmitting}
              />
            </DialogContent>
            <DialogActions>
              <Fade
                in={isSubmitting}
                style={{ transitionDelay: isSubmitting ? "800ms" : "0ms" }}
                unmountOnExit={true}
              >
                <CircularProgress color="secondary" size={20} />
              </Fade>

              <Button
                disabled={!isValid || isSubmitting}
                onClick={submitForm}
              >
                Submit
              </Button>
              <Button
                className={classes.disabledButton}
                onClick={() => this.service.send({ type: 'CloseUserDialog' })}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </form>
      </div>
    );
  };

  private validate = (values: IAddUserForm): FormikErrors<IAddUserForm> => {
    const errors: FormikErrors<IAddUserForm> = {};

    // EMAIL
    //1. Require
    // 2. Max length = 255
    // 3. Valid
    if (validator.isEmpty(values.email)) {
      errors.email = "Required";
    } else if (!validator.isLength(values.email, { min: 1, max: 75 })) {
      errors.email = "Email can be no longer than 75 characters";
    } else if (!validator.isEmail(values.email)) {
      errors.email = "Email is not in a valid format";
    }
    return errors;
  };

  private onSubmit = async (
    values: IAddUserForm,
    formikHelpers: FormikHelpers<IAddUserForm>
  ): Promise<void> => {
    const { setSubmitting } = formikHelpers;
    try {
      const retval = await firstValueFrom(
        UserManagerService.AddNewUserToSystem$(this.props.user, values.email)
      );
      if (retval instanceof AjaxError || !retval) {
        this.setRedirectState();
      }

      // Close the dialog
      this.service.send({ type: 'CloseUserDialog' });
    } catch (ex) {
    } finally {
      setSubmitting(false);
      this.RefreshModel();
    }
  };

  async updateUser(type: string) {
    try {
      this.setState({ isProcessing: true });
      if (type === "lock") {
        await firstValueFrom(
          UserManagerService.LockUser$(this.props.user, this.state.targetUser)
        );
        this.setState({ isUserLockedOut: true });
      }
      else if (type === "unlock") {
        await firstValueFrom(
          UserManagerService.UnLockUser$(this.props.user, this.state.targetUser)
        );
        this.setState({ isUserLockedOut: false });
      }
    } catch (ex) {
      const error = ex as AjaxError;
      this.setRedirectState();
      this.setState({
        errorMsg: ExceptionSerivce.StandardAjaxExceptionHandler(error)
      });
    }
    finally {
      this.setState({ isProcessing: false });
    }
  }
}

// REDUX CONTAINER
const AllUsersPageWithState = connect<IGenericUserProps>(
  genericMapStateToProps
)(AllUsersView);

// STYLES CONTAINER
const AllUsersPage = withStyles(styles)(AllUsersPageWithState);

export default AllUsersPage;
